/**
 * Sidebar filtering — `GET /user/permission`'s NavBarList narrowed to the
 * tenant's resolved modules (Part C, `src/shell/Sidebar.vue`). Pure functions
 * so the decision table is unit-testable without mounting a component.
 */
import type { NavBarItem, NavBarSubItem } from "@/app/types";
import type { ResolvedMfe } from "@/app/types";

export type NavModule = Pick<ResolvedMfe, "pathPrefix" | "allowedItems">;

function bareRoute(route: string): string {
  const i = route.indexOf("?");
  return i === -1 ? route : route.slice(0, i);
}

export function routeUnderPrefix(route: string, prefix: string): boolean {
  const bare = bareRoute(route);
  return bare === prefix || bare.startsWith(`${prefix}/`);
}

function moduleForRoute(route: string, modules: NavModule[]): NavModule | null {
  return modules.find((m) => routeUnderPrefix(route, m.pathPrefix)) || null;
}

/**
 * Filter one nav entry (top-level or a flattened `group: true` child — both
 * share the `{ name, icon, route, items? }` shape) against the tenant's
 * modules. Returns `null` when the entry's module isn't part of this tenant
 * app, or every one of its sub-items is outside `allowedItems`.
 */
export function filterEntry(item: NavBarItem, modules: NavModule[]): NavBarItem | null {
  const mod = moduleForRoute(item.route, modules);
  if (!mod) return null;

  if (mod.allowedItems == null) {
    // No sub-item restriction — the whole entry (and its drill list, if any) renders.
    return item;
  }

  if (!item.items || item.items.length === 0) {
    // A non-drillable entry can itself be gated by its own route.
    return mod.allowedItems.includes(item.route) ? item : null;
  }

  const allowed = item.items.filter((sub) => mod.allowedItems!.includes(sub.route));
  if (allowed.length === 0) return null;
  if (allowed.length === 1) {
    // Collapse to a single plain entry — labelled by the SECTION, not the module
    // (e.g. Orders limited to Stock Planner shows one "Stock Planner" row, per
    // the design spec's Verification checklist).
    const only = allowed[0] as NavBarSubItem;
    return { ...item, name: only.name, icon: only.icon || item.icon, route: only.route, items: undefined };
  }
  return { ...item, items: allowed };
}

/**
 * Filter the full NavBarList payload. `group: true` containers (e.g. "Tools")
 * have no route of their own — their children are flattened out and filtered
 * individually, same as any other top-level entry.
 */
export function filterNavForTenant(navItems: NavBarItem[], modules: NavModule[]): NavBarItem[] {
  const out: NavBarItem[] = [];
  for (const item of navItems) {
    if (item.group) {
      for (const child of item.items || []) {
        const filtered = filterEntry(child as NavBarItem, modules);
        if (filtered) out.push(filtered);
      }
      continue;
    }
    const filtered = filterEntry(item, modules);
    if (filtered) out.push(filtered);
  }
  return out;
}
